export type TrustIQBand = "Exposed" | "Inconsistent" | "Controlled" | "Fortified";

export interface TrustIQQuestion {
  id: string;
  question: string;
  options: { label: string; value: number }[];
  recommendation: string;
  feedback: { 0: string; 1: string; 2: string };
}

export const TRUSTIQ_QUESTIONS: TrustIQQuestion[] = [
  {
    id: "q1",
    question: "When a client requests a bank account change, how does your team verify their identity before processing it?",
    options: [
      { label: "We accept the request from any email or phone call", value: 0 },
      { label: "We call the client back on a number we already have on file", value: 1 },
      { label: "We use a formal identity-verification process with a digital audit trail", value: 2 },
    ],
    recommendation: "Verbal callbacks and email replies are insufficient — fraudsters can intercept both. Implement a formal verified change-request workflow with a tamper-evident audit trail before processing any sensitive update.",
    feedback: {
      0: "Email and phone requests are the entry point for 94% of BEC fraud in Australian real estate.",
      1: "Phone callbacks help, but fraudsters can forward calls or spoof numbers. A digital trail is harder to fake.",
      2: "A formal verified workflow with an audit trail is the gold standard. Strong control.",
    },
  },
  {
    id: "q2",
    question: "How do you handle a bank account update request that arrives via email attachment (e.g. a PDF form)?",
    options: [
      { label: "We process it as long as it looks legitimate", value: 0 },
      { label: "We follow up by phone before acting on it", value: 1 },
      { label: "We reject PDF-based requests and require a verified digital submission", value: 2 },
    ],
    recommendation: "PDF attachments carry no chain of custody. They can be intercepted and altered in transit. Require all sensitive change requests to be submitted through a platform that ties every submission to a verified identity.",
    feedback: {
      0: "PDF attachments carry zero chain of custody — they're the most common vehicle for payment fraud.",
      1: "A phone call reduces risk, but it can't prove the PDF wasn't altered in transit.",
      2: "Rejecting PDF requests removes a major attack vector entirely. Best-practice control.",
    },
  },
  {
    id: "q3",
    question: "Has your team received formal training on how to identify business email compromise (BEC) attacks?",
    options: [
      { label: "No formal training — we rely on common sense", value: 0 },
      { label: "We've had one-off awareness sessions but nothing structured", value: 1 },
      { label: "Yes — regular, documented training that covers current BEC tactics", value: 2 },
    ],
    recommendation: "BEC tactics evolve constantly. Ad-hoc awareness does not keep pace. Implement a regular training cycle that covers look-alike domains, email thread hijacking, and invoice fraud specific to your industry.",
    feedback: {
      0: "BEC tactics evolve faster than intuition. Without structured training, gaps are invisible until it's too late.",
      1: "One-off sessions are better than nothing — but attackers know gaps appear between refreshes.",
      2: "Regular, documented training is a verifiable control that insurers and regulators look for explicitly.",
    },
  },
  {
    id: "q4",
    question: "What happens after a bank account or contact detail change is processed? Who reviews it, and when?",
    options: [
      { label: "The person who processed it is responsible — no secondary check", value: 0 },
      { label: "A manager is notified but doesn't always review in time", value: 1 },
      { label: "Every change triggers an automated audit event and a mandatory second-person review", value: 2 },
    ],
    recommendation: "Segregation of duties is a foundational control. The person who receives a change request must not also be the person who approves it. Enforce a four-eyes rule with an immutable audit log for all sensitive updates.",
    feedback: {
      0: "A single-person process is the most common factor cited in successful payment fraud post-mortems.",
      1: "Notification without guaranteed review still leaves a window. Timing is the gap attackers exploit.",
      2: "Automated audit events plus mandatory second-person review is textbook segregation of duties.",
    },
  },
  {
    id: "q5",
    question: "If a fraudulent payment were made today based on a fake change request, how quickly could you reconstruct the full chain of events?",
    options: [
      { label: "It would be difficult — records are spread across email and spreadsheets", value: 0 },
      { label: "We could piece it together within a day or two from various systems", value: 1 },
      { label: "Immediately — every request has a time-stamped, immutable audit trail in one place", value: 2 },
    ],
    recommendation: "When fraud occurs, insurers and regulators ask for evidence of controls. If you cannot produce a clean audit trail within hours, you face both financial and compliance exposure. Centralise all change requests in a single auditable system.",
    feedback: {
      0: "Fragmented records across email and spreadsheets are the most common insurer complaint after fraud claims.",
      1: "A day-or-two reconstruction timeline often means evidence gaps — and gaps mean claim denials.",
      2: "Immediate, immutable audit trails are exactly what insurers and regulators audit for. This is the standard.",
    },
  },
  {
    id: "q6",
    question: "How confident are you that your team can spot a look-alike email domain (e.g. @acme-re.com.au vs @acme.com.au)?",
    options: [
      { label: "Not confident — it's easy to miss under time pressure", value: 0 },
      { label: "Somewhat confident — staff know to check but it's not always done", value: 1 },
      { label: "Confident — we have technical controls (DMARC, domain monitoring) as a backstop", value: 2 },
    ],
    recommendation: "Human vigilance alone fails under workload pressure. Layer technical controls — DMARC enforcement, look-alike domain alerting, and verified submission channels — so the system catches what eyes miss.",
    feedback: {
      0: "On settlement days, even experienced staff miss look-alike domains. Humans can't be the last line of defence.",
      1: "Human vigilance helps, but DMARC and domain monitoring catch what eyes miss under pressure.",
      2: "Technical backstops mean fraud defence doesn't depend on everyone having a perfect day. Strong control.",
    },
  },
  {
    id: "q7",
    question: "What is your current process for handling sensitive client data (bank details, personal information) once a change request is completed?",
    options: [
      { label: "Data stays in email threads or shared inboxes indefinitely", value: 0 },
      { label: "We try to delete it but there's no enforced policy", value: 1 },
      { label: "Data is stored in a compliant system with access controls and a defined retention policy", value: 2 },
    ],
    recommendation: "Sensitive data in unmanaged inboxes is a liability — it multiplies attack surface and creates regulatory exposure. Adopt a platform with role-based access control, encryption at rest, and an enforced data retention policy.",
    feedback: {
      0: "Unmanaged inboxes holding bank details multiply your attack surface and create Privacy Act exposure.",
      1: "An unenforced policy is equivalent to no policy when a breach investigation starts.",
      2: "Compliant storage with access controls and a defined retention policy is the minimum defensible standard.",
    },
  },
  {
    id: "q8",
    question: "Do you have cyber insurance, and does it specifically cover payment-redirection fraud losses?",
    options: [
      { label: "No cyber insurance", value: 0 },
      { label: "We have cyber insurance but haven't checked if BEC/payment fraud is covered", value: 1 },
      { label: "Yes — policy explicitly covers BEC and payment-redirection fraud, and we've reviewed it recently", value: 2 },
    ],
    recommendation: "Many standard cyber policies exclude payment-redirection fraud or require proof of controls before paying a claim. Review your policy wording with your broker and implement verifiable controls that satisfy insurer requirements.",
    feedback: {
      0: "Without cyber insurance, a successful fraud incident is an uninsured loss. Recoveries are rare.",
      1: "Many standard policies exclude BEC fraud. Unreviewed coverage may be no coverage at all.",
      2: "A reviewed, BEC-inclusive policy means you know your exposure. That's half the risk management job done.",
    },
  },
];

export interface TrustIQBandInfo {
  band: TrustIQBand;
  label: string;
  description: string;
  color: string;
  bgColor: string;
}

export const TRUSTIQ_BANDS: TrustIQBandInfo[] = [
  {
    band: "Exposed",
    label: "Exposed",
    description: "Your agency has significant gaps across identity verification, process controls, and staff awareness. A targeted fraud attempt would likely succeed and be difficult to trace.",
    color: "hsl(0, 65%, 45%)",
    bgColor: "hsl(0, 65%, 97%)",
  },
  {
    band: "Inconsistent",
    label: "Inconsistent",
    description: "Some controls are in place, but they're applied unevenly. A sophisticated attacker will find the gaps. Consistency and documentation are your biggest opportunities.",
    color: "hsl(35, 85%, 45%)",
    bgColor: "hsl(35, 85%, 97%)",
  },
  {
    band: "Controlled",
    label: "Controlled",
    description: "You have solid foundations. Most common attack vectors are covered, but there are areas to harden — particularly around audit trails and process enforcement.",
    color: "hsl(168, 25%, 35%)",
    bgColor: "hsl(168, 25%, 96%)",
  },
  {
    band: "Fortified",
    label: "Fortified",
    description: "Your agency has industry-leading fraud controls. You have formal processes, technical safeguards, and audit capability that would satisfy most insurer and regulator requirements.",
    color: "hsl(168, 35%, 28%)",
    bgColor: "hsl(168, 35%, 95%)",
  },
];

export function scoreTrustIQ(answers: Record<string, number>): {
  score: number;
  band: TrustIQBand;
  bandInfo: TrustIQBandInfo;
  weakestQuestion: TrustIQQuestion;
} {
  const score = Object.values(answers).reduce((sum, v) => sum + v, 0);
  const band: TrustIQBand =
    score <= 5 ? "Exposed" :
    score <= 10 ? "Inconsistent" :
    score <= 13 ? "Controlled" :
    "Fortified";
  const bandInfo = TRUSTIQ_BANDS.find((b) => b.band === band)!;

  const weakestEntry = TRUSTIQ_QUESTIONS.map((q) => ({
    q,
    score: answers[q.id] ?? 0,
  })).sort((a, b) => a.score - b.score)[0]!;

  return { score, band, bandInfo, weakestQuestion: weakestEntry.q };
}

export type RiskLevel = "Low" | "Elevated" | "High" | "Critical";

export interface RiskGaugeInfo {
  level: RiskLevel;
  color: string;
  bgColor: string;
  borderColor: string;
  fillPct: number;
}

export function computeRiskGauge(answers: Record<string, number>): RiskGaugeInfo {
  const answered = Object.keys(answers).length;
  if (answered === 0) {
    return { level: "Low", color: "hsl(168, 35%, 32%)", bgColor: "hsl(168, 35%, 96%)", borderColor: "hsl(168, 35%, 75%)", fillPct: 8 };
  }
  const maxPossible = answered * 2;
  const actual = Object.values(answers).reduce((s, v) => s + v, 0);
  const dangerRatio = (maxPossible - actual) / maxPossible;

  if (dangerRatio < 0.25) {
    return { level: "Low", color: "hsl(168, 35%, 32%)", bgColor: "hsl(168, 35%, 96%)", borderColor: "hsl(168, 35%, 75%)", fillPct: Math.round(8 + dangerRatio * 92) };
  } else if (dangerRatio < 0.5) {
    return { level: "Elevated", color: "hsl(42, 70%, 38%)", bgColor: "hsl(42, 70%, 96%)", borderColor: "hsl(42, 70%, 72%)", fillPct: Math.round(8 + dangerRatio * 92) };
  } else if (dangerRatio < 0.75) {
    return { level: "High", color: "hsl(25, 80%, 40%)", bgColor: "hsl(25, 80%, 97%)", borderColor: "hsl(25, 80%, 72%)", fillPct: Math.round(8 + dangerRatio * 92) };
  } else {
    return { level: "Critical", color: "hsl(0, 65%, 42%)", bgColor: "hsl(0, 65%, 97%)", borderColor: "hsl(0, 65%, 72%)", fillPct: Math.round(8 + dangerRatio * 92) };
  }
}
